/**
 * App.tsx — Root component with routing setup
 *
 * Route structure:
 * - /login                 → LoginPage (public)
 * - /                      → DashboardPage (protected)
 * - /pos                   → POS (protected, placeholder)
 * - /products              → Products (protected, placeholder)
 * - /customers             → Customers (protected, placeholder)
 * - /inventory             → Inventory (protected, placeholder)
 * - /transfers             → Transfers (protected)
 * - /cfdi                  → CFDI (protected, placeholder)
 * - /crm                   → dashboard CRM moderno
 * - /reports               → Reports (protected, placeholder)
 * - /admin                 → Admin (protected, admin only)
 * - *                      → NotFoundPage
 */

import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { Suspense, lazy, useEffect } from 'react';
import type { RutaProd } from './prod/runtime/rutas';
import { AuthProvider } from './lib/auth';
import { ProtectedRoute } from './components/ProtectedRoute';
import LoadingScreen from './components/LoadingScreen';
import { SWUpdateBanner } from './components/SWUpdateBanner';
import { OfflineIndicator, InstallPrompt } from './components/pwa';
import { GlobalErrorBoundary } from './components/ErrorBoundary';
import { ToasterProvider } from './components/ui';

// Eagerly loaded pages (critical path)
import LoginPage from './pages/login';
import NotFoundPage from './pages/not-found';
import DashboardPage from './pages/dashboard';

// Lazily loaded (code split per section)
const POSPage        = lazy(() => import('./pages/pos'));
const ProductsPage   = lazy(() => import('./pages/products'));
const CatalogsDashboardPage = lazy(() => import('./pages/catalogs-dashboard'));
const CatalogsUsersPage = lazy(() => import('./pages/catalogs-users'));
const CatalogsKitsPage = lazy(() => import('./pages/catalogs-kits'));
const CatalogsDynamicKitsPage = lazy(() => import('./pages/catalogs-dynamic-kits'));
const MedicalRecipePage = lazy(() => import('./pages/medical-recipe'));
const CustomersPage  = lazy(() => import('./pages/customers'));
const InventoryPage  = lazy(() => import('./pages/inventory'));
const InventoryBulkAdjustPage  = lazy(() => import('./pages/inventory-bulk-adjust'));
const InventoryAdjustmentsPage = lazy(() => import('./pages/inventory-adjustments'));
const InventoryReportPage      = lazy(() => import('./pages/inventory-report'));
const InventoryCategoriesPage  = lazy(() => import('./pages/inventory-categories'));
const KardexPage     = lazy(() => import('./pages/kardex'));
const TransfersPage  = lazy(() => import('./pages/transfers'));
const OrdersPage     = lazy(() => import('./pages/orders'));
const CFDIPage            = lazy(() => import('./pages/cfdi'));
const CfdiReportsPage     = lazy(() => import('./pages/cfdi-reports'));
const CfdiDashboardPage   = lazy(() => import('./pages/cfdi-dashboard'));
const GenerateInvoicePage = lazy(() => import('./pages/invoices/GenerateInvoicePage'));
const GlobalInvoicePage   = lazy(() => import('./pages/invoices/GlobalInvoicePage'));
const PaymentRepPage      = lazy(() => import('./pages/invoices/PaymentRepPage'));
const CreditNotePage      = lazy(() => import('./pages/invoices/CreditNotePage'));
const ManualInvoicePage   = lazy(() => import('./pages/invoices/ManualInvoicePage'));
const CrmDashboardPage = lazy(() => import('./pages/crm-dashboard'));
const CrmClientsPage = lazy(() => import('./pages/crm-clients'));
const CrmPipelinePage = lazy(() => import('./pages/crm-pipeline'));
const CrmTasksPage   = lazy(() => import('./pages/crm-tasks'));
const CrmInteractionsPage = lazy(() => import('./pages/crm-interactions'));
const CrmChatsPage   = lazy(() => import('./pages/crm-chats'));
const CrmTagsPage    = lazy(() => import('./pages/crm-tags'));
const PromotionsPage = lazy(() => import('./pages/promotions'));
const ReportsPage    = lazy(() => import('./pages/reports'));
const StoreDashboardPage = lazy(() => import('./pages/store-dashboard'));
const StoreOrdersPage    = lazy(() => import('./pages/store-orders'));
const StoreProductsPage  = lazy(() => import('./pages/store-products'));
const StoreSettingsPage  = lazy(() => import('./pages/store-settings'));
const BIPage             = lazy(() => import('./pages/bi'));
const ReportesDinamicosPage = lazy(() => import('./pages/reportes-dinamicos'));
const ProcurementPage    = lazy(() => import('./pages/procurement'));
const AdminPage          = lazy(() => import('./pages/admin/AdminPage'));
const LayawaysPage       = lazy(() => import('./pages/layaways'));
const QuotationsPage     = lazy(() => import('./pages/quotations'));
const QuotationsNewPage       = lazy(() => import('./pages/quotations-new'));
const QuotationsDashboardPage = lazy(() => import('./pages/quotations-dashboard'));
const POSSettingsGeneralPage     = lazy(() => import('./pages/pos-settings-general'));
const POSSettingsVentasPage      = lazy(() => import('./pages/pos-settings-ventas'));
const POSSettingsTicketsPage     = lazy(() => import('./pages/pos-settings-tickets'));
const POSSettingsProductosPage   = lazy(() => import('./pages/pos-settings-productos'));
const POSSettingsClientesPage    = lazy(() => import('./pages/pos-settings-clientes'));
const POSSettingsComprasPage     = lazy(() => import('./pages/pos-settings-compras'));
const POSSettingsFacturacionPage = lazy(() => import('./pages/pos-settings-facturacion'));

/**
 * Rutas viejas que ya tienen su documento /sistema (plan §1.6): cada `<Vista>.ruta.ts` las declara en `rutasViejas`
 * cuando TODAS sus filas están en VERIFICADO (docs/parity/ui/rutas-viejas.json), y aquí se descubren por glob. Se va con
 * `location.replace`, no con <Navigate>: una ruta /sistema/... entraría a este router y caería en `*` → /404.
 */
export function redireccionesViejas(rutas: Record<string, RutaProd>): Array<{ vieja: string; destino: string }> {
  const out: Array<{ vieja: string; destino: string }> = [];
  for (const r of Object.values(rutas)) {
    for (const vieja of r.rutasViejas ?? []) {
      if (!vieja.startsWith('/') || vieja.startsWith('/sistema')) throw new Error(`[App] ruta vieja inválida en ${r.ruta}: ${vieja}`);
      if (out.some((x) => x.vieja === vieja)) throw new Error(`[App] la ruta vieja ${vieja} tiene dos destinos`);
      out.push({ vieja, destino: r.ruta });
    }
  }
  return out;
}
const RUTAS_PROD = import.meta.glob<RutaProd>('./prod/areas/*/Views/**/*.ruta.ts', { eager: true, import: 'default' });
export const REDIRECCIONES_VIEJAS: ReadonlyArray<{ vieja: string; destino: string }> = redireccionesViejas(RUTAS_PROD);

/** `location.replace` al documento, con la misma query. */
export function IrADocumento({ destino }: { destino: string }): null {
  const { search } = useLocation();
  useEffect(() => { window.location.replace(destino + search); }, [destino, search]);
  return null;
}

export default function App(): React.ReactElement {
  return (
    <GlobalErrorBoundary>
    <ToasterProvider>
    <AuthProvider>
      <BrowserRouter>
        {/* PWA: offline indicator + install prompt + SW update banner */}
        <OfflineIndicator />
        <InstallPrompt />
        <SWUpdateBanner />
        <Suspense fallback={<LoadingScreen />}>
          <Routes>
            {/* Rutas viejas ya portadas a /sistema (antes que las demás: en un empate gana la primera) */}
            {REDIRECCIONES_VIEJAS.map(({ vieja, destino }) => (
              <Route key={vieja} path={vieja} element={<IrADocumento destino={destino} />} />
            ))}

            {/* Public routes */}
            <Route path="/login" element={<LoginPage />} />

            {/* Protected routes — require authentication */}
            <Route element={<ProtectedRoute />}>
              <Route path="/"          element={<DashboardPage />} />
              <Route path="/pos"       element={<POSPage />} />
              <Route path="/pos/medical-recipe" element={<MedicalRecipePage />} />
              {/* Configuración del POS — páginas separadas (paridad con prod) */}
              <Route path="/pos/settings" element={<Navigate to="/pos/settings/general" replace />} />
              <Route path="/pos/settings/general"     element={<POSSettingsGeneralPage />} />
              <Route path="/pos/settings/ventas"      element={<POSSettingsVentasPage />} />
              <Route path="/pos/settings/tickets"     element={<POSSettingsTicketsPage />} />
              <Route path="/pos/settings/productos"   element={<POSSettingsProductosPage />} />
              <Route path="/pos/settings/clientes"    element={<POSSettingsClientesPage />} />
              <Route path="/pos/settings/compras"     element={<POSSettingsComprasPage />} />
              <Route path="/pos/settings/facturacion" element={<POSSettingsFacturacionPage />} />
              <Route path="/products"  element={<ProductsPage />} />
              <Route path="/catalogs/dashboard" element={<CatalogsDashboardPage />} />
              <Route path="/catalogs/users" element={<CatalogsUsersPage />} />
              <Route path="/catalogs/kits" element={<CatalogsKitsPage />} />
              <Route path="/catalogs/dynamic-kits" element={<CatalogsDynamicKitsPage />} />
              <Route path="/customers" element={<CustomersPage />} />
              <Route path="/inventory" element={<InventoryPage />} />
              <Route path="/inventory/bulk-adjust" element={<InventoryBulkAdjustPage />} />
              <Route path="/inventory/adjustments" element={<InventoryAdjustmentsPage />} />
              <Route path="/inventory/report" element={<InventoryReportPage />} />
              <Route path="/inventory/categories" element={<InventoryCategoriesPage />} />
              <Route path="/inventory/kardex" element={<KardexPage />} />
              <Route path="/transfers" element={<TransfersPage />} />
              <Route path="/orders"    element={<OrdersPage />} />
              <Route path="/cfdi"      element={<CFDIPage />} />
              <Route path="/cfdi/dashboard" element={<CfdiDashboardPage />} />
              <Route path="/cfdi/reports"   element={<CfdiReportsPage />} />
              <Route path="/cfdi/generate" element={<GenerateInvoicePage />} />
              <Route path="/cfdi/invoice-manual" element={<ManualInvoicePage />} />
              <Route path="/cfdi/credit-note" element={<CreditNotePage />} />
              <Route path="/cfdi/global"   element={<GlobalInvoicePage />} />
              <Route path="/cfdi/payment"  element={<PaymentRepPage />} />
              <Route path="/crm"          element={<Navigate to="/crm/dashboard" replace />} />
              <Route path="/crm/dashboard" element={<CrmDashboardPage />} />
              <Route path="/crm/clients"  element={<CrmClientsPage />} />
              <Route path="/crm/pipeline" element={<CrmPipelinePage />} />
              <Route path="/crm/tasks"        element={<CrmTasksPage />} />
              <Route path="/crm/interactions" element={<CrmInteractionsPage />} />
              <Route path="/crm/chats"        element={<CrmChatsPage />} />
              <Route path="/crm/tags"         element={<CrmTagsPage />} />
              <Route path="/promotions"  element={<PromotionsPage />} />
              <Route path="/reports"   element={<ReportsPage />} />
              <Route path="/store"          element={<StoreDashboardPage />} />
              <Route path="/store/orders"   element={<StoreOrdersPage />} />
              <Route path="/store/products" element={<StoreProductsPage />} />
              <Route path="/store/settings" element={<StoreSettingsPage />} />
              <Route path="/bi"           element={<BIPage />} />
              <Route path="/reportes-dinamicos" element={<ReportesDinamicosPage />} />
              <Route path="/procurement" element={<ProcurementPage />} />

              <Route path="/layaways" element={<LayawaysPage />} />
              <Route path="/quotations" element={<QuotationsPage />} />
              <Route path="/quotations/new" element={<QuotationsNewPage />} />
              <Route path="/quotations/dashboard" element={<QuotationsDashboardPage />} />

              {/* Admin only */}
              <Route element={<ProtectedRoute superAdminOnly />}>
                <Route path="/admin/*" element={<AdminPage />} />
              </Route>
            </Route>

            {/* Catch-all */}
            <Route path="/404" element={<NotFoundPage />} />
            <Route path="*"    element={<Navigate to="/404" replace />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </AuthProvider>
    </ToasterProvider>
    </GlobalErrorBoundary>
  );
}
